import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TrendingUp, BarChart3, Percent, Gift, Star, ArrowUpRight, AlertTriangle, X, RefreshCw, CheckCircle2 } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, BarChart, Bar, Area, AreaChart
} from "recharts";
import { getBrandPillStyle } from "@/lib/brandColors";
import { buildItemCodeMap, calcFreebieKpis } from "@/lib/freebieCalc";
import { DateRangeFilter } from "@/components/DateRangeFilter";
import QuotationTypeToggle from "@/components/QuotationTypeToggle";
import { useQuotationTypeView, matchesQuotationType } from "@/hooks/useQuotationTypeView";

const BRAND_COLORS = { Wellcore: "#14b8a6", Dynamite: "#f97316", Okami: "#8b5cf6", YouWeFit: "#3b82f6", "Zero Sugar": "#ec4899", OSMO: "#10b981", Unknown: "#94a3b8" };
const PIE_COLORS = ["#14b8a6", "#f97316", "#8b5cf6", "#3b82f6", "#ec4899", "#10b981", "#94a3b8"];
const ALL_BRANDS = ["Wellcore", "Dynamite", "Okami", "YouWeFit", "Zero Sugar", "OSMO"];

const CARD_COLORS = { teal: "#16A36A", blue: "#2878D4", purple: "#8A4DE8", amber: "#F07A16", coral: "#F04464" };
const CARD_BG = { teal: "#F0FAF5", blue: "#F1F7FF", purple: "#F7F2FF", amber: "#FFF6ED", coral: "#FFF2F4" };

const formatCurrency = (v) => { if (v >= 1e7) return `₹${(v/1e7).toFixed(2)} Cr`; if (v >= 1e5) return `₹${(v/1e5).toFixed(2)} L`; if (v >= 1e3) return `₹${(v/1e3).toFixed(1)} K`; return `₹${v.toLocaleString("en-IN")}`; };
const fmtPct = (v) => `${Number(v).toFixed(2)}%`;

// Tiny inline sparkline using SVG
function Sparkline({ data, color, width = 80, height = 28 }) {
  if (!data || data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * width},${height - ((v - min) / range) * (height - 4) - 2}`);
  return (
    <svg width={width} height={height} className="shrink-0">
      <polyline points={pts.join(" ")} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Delta badge. When there is no data in the previous period, shows a muted
// "No previous period data" note instead of a computed delta.
function Delta({ current, previous, isPercent = false, warningThreshold, hasPrevData = true }) {
  if (warningThreshold != null && current >= warningThreshold) {
    return <span className="inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded" style={{ color: "#BA7517", background: "#FAEEDA" }}><AlertTriangle className="h-3 w-3" />Above {warningThreshold}% threshold</span>;
  }
  if (!hasPrevData) {
    return <span className="text-[11px] italic" style={{ color: "#888780" }}>No previous period data</span>;
  }
  const diff = isPercent ? current - previous : (previous !== 0 ? ((current - previous) / previous) * 100 : 100);
  const isUp = diff >= 0;
  return (
    <span className={`text-[11px] font-medium ${isUp ? "text-green-600" : "text-red-500"}`}>
      {isUp ? "↑" : "↓"} {Math.abs(diff).toFixed(1)}{isPercent ? " pp" : "%"} vs prev period
    </span>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [allQuotations, setAllQuotations] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [allSellers, setAllSellers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Default to current month
  const now = new Date();
  const monthStart = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-01`;
  const today = now.toISOString().substring(0,10);

  // Filters
  const [dateFrom, setDateFrom] = useState(monthStart);
  const [dateTo, setDateTo] = useState(today);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedSkus, setSelectedSkus] = useState([]);
  const [typeView] = useQuotationTypeView();
  const [brandFilterOpen, setBrandFilterOpen] = useState(false);
  const [skuFilterOpen, setSkuFilterOpen] = useState(false);
  const [timeGranularity, setTimeGranularity] = useState("day");

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [qRes, pRes, sRes] = await Promise.all([
        axios.get(`${API}/quotations`, { withCredentials: true }),
        axios.get(`${API}/products`, { withCredentials: true }),
        axios.get(`${API}/sellers`, { withCredentials: true })
      ]);
      setAllQuotations(qRes.data || []);
      setAllProducts(pRes.data || []);
      setAllSellers(sRes.data || []);
    } catch { /* silent */ } finally { setLoading(false); }
  };

  const sellerMap = useMemo(() => {
    const m = {};
    allSellers.forEach(s => { m[s.id] = s; });
    return m;
  }, [allSellers]);

  // ---- FILTERED QUOTATIONS ----
  const filteredQuotations = useMemo(() => {
    let f = [...allQuotations];
    // Analytics-wide rule: only invoiced quotations are counted, and filtering is by invoice_date.
    f = f.filter(q => !!q.invoice_date);
    if (typeView !== "all") f = f.filter(q => matchesQuotationType(q, typeView));
    if (dateFrom) f = f.filter(q => (q.invoice_date?.substring(0,10) || "") >= dateFrom);
    if (dateTo) f = f.filter(q => (q.invoice_date?.substring(0,10) || "") <= dateTo);
    if (selectedBrands.length > 0) f = f.filter(q => [...(q.items||[]),...(q.freebies||[])].some(i => selectedBrands.includes(i.brand)));
    if (selectedSkus.length > 0) f = f.filter(q => [...(q.items||[]),...(q.freebies||[])].some(i => selectedSkus.includes(i.sku_id)));
    return f;
  }, [allQuotations, dateFrom, dateTo, selectedBrands, selectedSkus, typeView]);

  // ---- PREVIOUS PERIOD (same-length window immediately before [dateFrom, dateTo]) ----
  // Uses the same brand/SKU filters as the current period so the delta is apples-to-apples.
  const prevPeriod = useMemo(() => {
    if (!dateFrom || !dateTo) return [];
    const fromDate = new Date(`${dateFrom}T00:00:00Z`);
    const toDate = new Date(`${dateTo}T00:00:00Z`);
    if (isNaN(fromDate) || isNaN(toDate) || toDate < fromDate) return [];
    const lengthDays = Math.round((toDate - fromDate) / 86400000) + 1;
    const prevToDate = new Date(fromDate.getTime() - 86400000);
    const prevFromDate = new Date(prevToDate.getTime() - (lengthDays - 1) * 86400000);
    const prevFrom = prevFromDate.toISOString().substring(0, 10);
    const prevTo = prevToDate.toISOString().substring(0, 10);

    return allQuotations.filter(q => {
      const d = q.invoice_date?.substring(0, 10) || "";
      if (d < prevFrom || d > prevTo) return false;
      if (!matchesQuotationType(q, typeView)) return false;
      if (selectedBrands.length > 0 && ![...(q.items||[]), ...(q.freebies||[])].some(i => selectedBrands.includes(i.brand))) return false;
      if (selectedSkus.length > 0 && ![...(q.items||[]), ...(q.freebies||[])].some(i => selectedSkus.includes(i.sku_id))) return false;
      return true;
    });
  }, [allQuotations, dateFrom, dateTo, selectedBrands, selectedSkus, typeView]);

  // ---- KPI HELPERS ----
  const itemCodeMap = useMemo(() => buildItemCodeMap(allProducts), [allProducts]);

  const calcKPIs = (qs) => {
    let rev = 0;
    // Avg. Effective Discount is a VALUE-WEIGHTED average that includes freebie MRP
    // in the base: Σ(eligibleMRP − invoiceValue) / Σ(eligibleMRP), where
    // eligibleMRP = total_mrp_value + freebies_value. This matches the Discount
    // Analytics methodology and avoids over-weighting tiny invoices.
    let discNum = 0, discDen = 0;
    qs.forEach(q => {
      rev += q.total_invoice_value || 0;
      const eligibleMrp = (q.total_mrp_value || 0) + (q.freebies_value || 0);
      if (eligibleMrp > 0) {
        discNum += eligibleMrp - (q.total_invoice_value || 0);
        discDen += eligibleMrp;
      }
    });
    const count = qs.length || 1;
    // NEW freebie formula: filter line items by item_code prefix (WHFG / WHMC)
    const { totalFreebieValue, avgFreebiePct } = calcFreebieKpis(qs, itemCodeMap);
    return {
      totalRevenue: rev,
      aov: rev / count,
      avgDiscount: discDen > 0 ? (discNum / discDen) * 100 : 0,
      avgFreebiePct,
      totalFreebieValue,
      count
    };
  };

  const kpiCurrent = useMemo(() => calcKPIs(filteredQuotations), [filteredQuotations, itemCodeMap]);
  const kpiPrev = useMemo(() => calcKPIs(prevPeriod), [prevPeriod, itemCodeMap]);

  // ---- SPARKLINE DATA (last 7 days) ----
  const sparklines = useMemo(() => {
    const today = new Date();
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      days.push(d.toISOString().substring(0,10));
    }
    const buckets = days.map(day => {
      const qs = allQuotations.filter(q => q.invoice_date?.substring(0,10) === day);
      const k = calcKPIs(qs);
      return { day, ...k };
    });
    return {
      revenue: buckets.map(b => b.totalRevenue),
      aov: buckets.map(b => b.aov),
      discount: buckets.map(b => b.avgDiscount),
      freebiePct: buckets.map(b => b.avgFreebiePct),
      freebieVal: buckets.map(b => b.totalFreebieValue),
    };
  }, [allQuotations, itemCodeMap]);

  // ---- UNIQUE SKUS ----
  const uniqueSkus = useMemo(() => {
    const m = new Map();
    allProducts.forEach(p => { if (p.sku_id) m.set(p.sku_id, { sku_id: p.sku_id, product_name: p.product_name, brand: p.brand }); });
    return Array.from(m.values()).sort((a,b) => a.product_name?.localeCompare(b.product_name));
  }, [allProducts]);

  const toggleBrand = (b) => setSelectedBrands(p => p.includes(b) ? p.filter(x=>x!==b) : [...p, b]);
  const toggleSku = (s) => setSelectedSkus(p => p.includes(s) ? p.filter(x=>x!==s) : [...p, s]);
  const clearFilters = () => { setDateFrom(monthStart); setDateTo(today); setSelectedBrands([]); setSelectedSkus([]); };
  const hasActiveFilters = dateFrom || dateTo || selectedBrands.length > 0 || selectedSkus.length > 0;

  // ---- CARDS CONFIG ----
  const cards = [
    { key: "revenue", label: "Total Revenue", value: formatCurrency(kpiCurrent.totalRevenue), color: CARD_COLORS.teal, bg: CARD_BG.teal, icon: TrendingUp, route: "/revenue-analytics", delta: <Delta current={kpiCurrent.totalRevenue} previous={kpiPrev.totalRevenue} hasPrevData={prevPeriod.length > 0} />, spark: sparklines.revenue },
    { key: "aov", label: "Avg. Order Value", value: formatCurrency(kpiCurrent.aov), color: CARD_COLORS.blue, bg: CARD_BG.blue, icon: BarChart3, route: "/revenue-analytics", delta: <Delta current={kpiCurrent.aov} previous={kpiPrev.aov} hasPrevData={prevPeriod.length > 0} />, spark: sparklines.aov },
    { key: "discount", label: "Avg. Effective Discount", value: fmtPct(kpiCurrent.avgDiscount), color: CARD_COLORS.purple, bg: CARD_BG.purple, icon: Percent, route: "/discount-analytics", delta: <Delta current={kpiCurrent.avgDiscount} previous={kpiPrev.avgDiscount} isPercent warningThreshold={62} hasPrevData={prevPeriod.length > 0} />, spark: sparklines.discount, warn: kpiCurrent.avgDiscount >= 62 },
    { key: "freebiePct", label: "Avg. Freebie %", value: fmtPct(kpiCurrent.avgFreebiePct), color: CARD_COLORS.amber, bg: CARD_BG.amber, icon: Gift, route: "/freebie-analytics", delta: <Delta current={kpiCurrent.avgFreebiePct} previous={kpiPrev.avgFreebiePct} isPercent hasPrevData={prevPeriod.length > 0} />, spark: sparklines.freebiePct },
    { key: "freebieVal", label: "Total Freebie Value", value: formatCurrency(kpiCurrent.totalFreebieValue), color: CARD_COLORS.coral, bg: CARD_BG.coral, icon: Star, route: "/freebie-analytics", delta: <Delta current={kpiCurrent.totalFreebieValue} previous={kpiPrev.totalFreebieValue} hasPrevData={prevPeriod.length > 0} />, spark: sparklines.freebieVal },
  ];

  // ---- FILTER BAR (shared) ----
  const FilterCheckItem = ({ checked, label, onClick }) => (
    <div className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-slate-100 ${checked ? 'bg-accent/10' : ''}`} onClick={onClick}>
      <div className={`w-4 h-4 rounded border ${checked ? 'bg-accent border-accent' : 'border-slate-300'}`}>{checked && <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>}</div>
      <span className="text-sm">{label}</span>
    </div>
  );

  const filterBar = (
    <div className="bg-white pb-4 mb-1 border-b border-[#E8E8E8]">
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-xl font-semibold text-[#0F0F0F] shrink-0 mr-auto" data-testid="dashboard-title">Dashboard</h1>
        <DateRangeFilter
          from={dateFrom}
          to={dateTo}
          onChange={(f,t)=>{ setDateFrom(f); setDateTo(t); }}
          testId="dashboard-date-range"
        />
        <QuotationTypeToggle testId="dashboard-qtype" />
        <Popover open={brandFilterOpen} onOpenChange={setBrandFilterOpen}>
          <PopoverTrigger asChild><Button variant="outline" className="h-8 text-xs px-3 border-[#E8E8E8]" data-testid="filter-brand">{selectedBrands.length === 0 ? "All Brands" : `${selectedBrands.length} Brand${selectedBrands.length>1?"s":""}`}</Button></PopoverTrigger>
          <PopoverContent className="w-56 p-2 z-50"><div className="space-y-1">{ALL_BRANDS.map(b=><FilterCheckItem key={b} checked={selectedBrands.includes(b)} label={b} onClick={()=>toggleBrand(b)} />)}</div></PopoverContent>
        </Popover>
        <Popover open={skuFilterOpen} onOpenChange={setSkuFilterOpen}>
          <PopoverTrigger asChild><Button variant="outline" className="h-8 text-xs px-3 border-[#E8E8E8]" data-testid="filter-sku">{selectedSkus.length===0?"All SKUs":`${selectedSkus.length} SKU${selectedSkus.length>1?"s":""}`}</Button></PopoverTrigger>
          <PopoverContent className="w-72 p-2 z-50"><div className="max-h-64 overflow-y-auto space-y-1">{uniqueSkus.slice(0,50).map(s=>(<div key={s.sku_id} className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-slate-100 ${selectedSkus.includes(s.sku_id)?'bg-accent/10':''}`} onClick={()=>toggleSku(s.sku_id)}><div className={`w-4 h-4 rounded border flex-shrink-0 ${selectedSkus.includes(s.sku_id)?'bg-accent border-accent':'border-slate-300'}`}>{selectedSkus.includes(s.sku_id)&&<svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>}</div><div className="flex-1 min-w-0"><p className="text-sm truncate">{s.product_name}</p><p className="text-xs text-muted-foreground">{s.sku_id}</p></div></div>))}</div></PopoverContent>
        </Popover>
        {hasActiveFilters && <Button variant="ghost" size="sm" onClick={clearFilters} className="h-8 px-2 text-xs text-red-500 hover:bg-red-50"><X className="h-3 w-3 mr-1" />Clear</Button>}
        <Button variant="outline" size="sm" onClick={fetchData} className="h-8 px-2 border-[#E8E8E8]" data-testid="refresh-dashboard"><RefreshCw className="h-3.5 w-3.5" /></Button>
      </div>
    </div>
  );

  if (loading) return (
    <div className="space-y-6">
      <div><h1 className="text-xl font-semibold text-[#0F0F0F]" data-testid="dashboard-title">Dashboard</h1></div>
      <div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent" /></div>
    </div>
  );

  // ==========================
  // OVERVIEW TAB (default)
  // ==========================
  return <OverviewTab filterBar={filterBar} cards={cards} navigate={navigate} filteredQuotations={filteredQuotations} sellerMap={sellerMap} allProducts={allProducts} kpiCurrent={kpiCurrent} selectedBrands={selectedBrands} selectedSkus={selectedSkus} timeGranularity={timeGranularity} setTimeGranularity={setTimeGranularity} hasActiveFilters={hasActiveFilters} dateFrom={dateFrom} dateTo={dateTo} />;
}

// ========= OVERVIEW TAB =========
function OverviewTab({ filterBar, cards, navigate, filteredQuotations, sellerMap, allProducts, selectedBrands, selectedSkus, timeGranularity, setTimeGranularity, hasActiveFilters, dateFrom, dateTo }) {
  // Build daily buckets first, then aggregate by granularity
  const salesTrendData = useMemo(() => {
    const byDate = {};
    filteredQuotations.forEach(q => {
      const d = q.invoice_date?.substring(0,10);
      if (!d) return;
      let saleItems = q.items || [];
      let freebItems = q.freebies || [];
      if (selectedBrands.length > 0) { saleItems = saleItems.filter(i=>selectedBrands.includes(i.brand)); freebItems = freebItems.filter(i=>selectedBrands.includes(i.brand)); }
      if (selectedSkus.length > 0) { saleItems = saleItems.filter(i=>selectedSkus.includes(i.sku_id)); freebItems = freebItems.filter(i=>selectedSkus.includes(i.sku_id)); }
      const rev = selectedBrands.length>0||selectedSkus.length>0 ? saleItems.reduce((s,i)=>s+(i.invoice_value||0),0) : (q.total_invoice_value||0);
      const qty = saleItems.reduce((s,i)=>s+(i.quantity||0),0);
      const freeQty = freebItems.reduce((s,i)=>s+(i.quantity||0),0);
      if (!byDate[d]) byDate[d]={revenue:0,qtySold:0,freebieQty:0};
      byDate[d].revenue += rev;
      byDate[d].qtySold += qty;
      byDate[d].freebieQty += freeQty;
    });

    const sorted = Object.entries(byDate).sort(([a],[b])=>a.localeCompare(b));
    if (sorted.length === 0) return [];

    if (timeGranularity === "day") {
      return sorted.map(([d,v])=>({ date: d, label: new Date(d+"T00:00:00").toLocaleDateString("en-IN",{day:"2-digit",month:"short"}), ...v }));
    }
    if (timeGranularity === "week") {
      const weeks = {};
      sorted.forEach(([d,v]) => {
        const dt = new Date(d+"T00:00:00");
        const day = dt.getDay(); const diff = day === 0 ? -6 : 1 - day;
        const mon = new Date(dt); mon.setDate(dt.getDate()+diff);
        const sun = new Date(mon); sun.setDate(mon.getDate()+6);
        const key = mon.toISOString().substring(0,10);
        const lbl = `${mon.toLocaleDateString("en-IN",{day:"2-digit",month:"short"})} – ${sun.toLocaleDateString("en-IN",{day:"2-digit",month:"short"})}`;
        if (!weeks[key]) weeks[key]={date:key,label:lbl,revenue:0,qtySold:0,freebieQty:0};
        weeks[key].revenue+=v.revenue; weeks[key].qtySold+=v.qtySold; weeks[key].freebieQty+=v.freebieQty;
      });
      return Object.values(weeks).sort((a,b)=>a.date.localeCompare(b.date));
    }
    // monthly
    const months = {};
    sorted.forEach(([d,v]) => {
      const key = d.substring(0,7);
      const lbl = new Date(d+"T00:00:00").toLocaleDateString("en-IN",{month:"short",year:"numeric"});
      if (!months[key]) months[key]={date:key,label:lbl,revenue:0,qtySold:0,freebieQty:0};
      months[key].revenue+=v.revenue; months[key].qtySold+=v.qtySold; months[key].freebieQty+=v.freebieQty;
    });
    return Object.values(months).sort((a,b)=>a.date.localeCompare(b.date));
  }, [filteredQuotations, selectedBrands, selectedSkus, timeGranularity]);

  // Date range label — reads directly from global filter inputs
  const dateRangeLabel = useMemo(() => {
    const fmtD = (d) => new Date(d+"T00:00:00").toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"});
    if (dateFrom && dateTo) return `Showing data from ${fmtD(dateFrom)} to ${fmtD(dateTo)}`;
    if (dateFrom) return `Showing data from ${fmtD(dateFrom)}`;
    if (dateTo) return `Showing data to ${fmtD(dateTo)}`;
    // Fallback: show full available data range
    const dates = filteredQuotations.map(q => q.invoice_date?.substring(0,10)).filter(Boolean).sort();
    if (dates.length === 0) return "";
    return `Showing data from ${fmtD(dates[0])} to ${fmtD(dates[dates.length-1])}`;
  }, [dateFrom, dateTo, filteredQuotations]);

  // Custom tooltip
  const SalesTrendTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0]?.payload;
    return (
      <div className="bg-white border border-[#E8E8E8] rounded-lg p-2.5 shadow-md min-w-[160px]">
        <p className="text-xs font-semibold text-[#0F0F0F] mb-1.5">{label}</p>
        <div className="space-y-1">
          <div className="flex justify-between gap-4"><span className="text-[11px]" style={{color:"#1D9E75"}}>Revenue</span><span className="text-[11px] font-mono font-medium" style={{color:"#1D9E75"}}>{formatCurrency(d?.revenue||0)}</span></div>
          <div className="flex justify-between gap-4"><span className="text-[11px]" style={{color:"#3b82f6"}}>Qty. Sold</span><span className="text-[11px] font-mono font-medium" style={{color:"#3b82f6"}}>{(d?.qtySold||0).toLocaleString("en-IN")} units</span></div>
          <div className="flex justify-between gap-4"><span className="text-[11px]" style={{color:"#d97706"}}>Freebie Qty.</span><span className="text-[11px] font-mono font-medium" style={{color:"#d97706"}}>{(d?.freebieQty||0).toLocaleString("en-IN")} units</span></div>
        </div>
      </div>
    );
  };

  // Top Sellers bar chart tooltip (Seller Name + Revenue + Contribution %)
  const TopSellersTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0]?.payload;
    return (
      <div
        className="bg-white rounded-lg shadow-md"
        style={{ border: "0.5px solid #E8E8E8", padding: "10px 14px", minWidth: 200 }}
      >
        <p style={{ color: "#0F0F0F", fontSize: 13, fontWeight: 500, marginBottom: 6 }}>{d?.name}</p>
        <div className="space-y-1">
          <div className="flex justify-between gap-6">
            <span className="text-[12px]" style={{ color: "#32B4AC" }}>Revenue</span>
            <span className="text-[12px] font-mono font-medium" style={{ color: "#32B4AC" }}>
              {formatCurrency(d?.revenue || 0)}
            </span>
          </div>
          <div className="flex justify-between gap-6">
            <span className="text-[12px]" style={{ color: "#32B4AC" }}>Contribution %</span>
            <span className="text-[12px] font-mono font-medium" style={{ color: "#32B4AC" }}>
              {(d?.contributionPct || 0).toFixed(2)}%
            </span>
          </div>
        </div>
      </div>
    );
  };

  const brandData = useMemo(() => {
    const bs = {};
    filteredQuotations.forEach(q => {
      let items = q.items||[];
      if (selectedSkus.length>0) items = items.filter(i=>selectedSkus.includes(i.sku_id));
      items.forEach(i => {
        const b = i.brand;
        if (!b) return; // skip unknown/unmapped brands entirely
        if (selectedBrands.length>0&&!selectedBrands.includes(b)) return;
        if (!bs[b]) bs[b]={revenue:0,count:0};
        bs[b].revenue+=i.invoice_value||0;
        bs[b].count++;
      });
    });
    const tot = Object.values(bs).reduce((s,b)=>s+b.revenue,0);
    return Object.entries(bs).map(([n,s])=>({name:n,value:s.revenue,count:s.count,percentage:tot>0?((s.revenue/tot)*100).toFixed(1):0})).sort((a,b)=>b.value-a.value);
  }, [filteredQuotations, selectedBrands, selectedSkus]);

  // Top 10 sellers by revenue (grouped by seller_name to merge duplicates) + contribution %.
  // Denominator = total revenue across ALL sellers in the currently filtered dataset.
  const { topSellers } = useMemo(() => {
    const m = {};
    filteredQuotations.forEach(q => {
      const name = (sellerMap[q.seller_id]?.seller_name || q.seller_name || "Unknown").trim();
      if (!m[name]) m[name] = { name, revenue: 0 };
      m[name].revenue += q.total_invoice_value || 0;
    });
    const all = Object.values(m);
    const total = all.reduce((s, x) => s + (x.revenue || 0), 0);
    const withShare = all.map(s => ({
      ...s,
      contributionPct: total > 0 ? (s.revenue / total) * 100 : 0,
    }));
    return {
      topSellers: withShare.sort((a, b) => b.revenue - a.revenue).slice(0, 10),
      allSellersTotalRevenue: total,
    };
  }, [filteredQuotations, sellerMap]);

  // Sellers above 62% avg discount (grouped by seller_name)
  const discountAlertSellers = useMemo(() => {
    const m = {};
    filteredQuotations.forEach(q => {
      const name = (sellerMap[q.seller_id]?.seller_name || q.seller_name || "Unknown").trim();
      if (!m[name]) m[name] = { name, discounts: [], invoiceCount: 0 };
      if (q.effective_discount != null) m[name].discounts.push(q.effective_discount);
      m[name].invoiceCount++;
    });
    return Object.values(m)
      .map(s => ({ ...s, avgDiscount: s.discounts.length > 0 ? s.discounts.reduce((a, b) => a + b, 0) / s.discounts.length : 0 }))
      .filter(s => s.avgDiscount >= 62)
      .sort((a, b) => b.avgDiscount - a.avgDiscount);
  }, [filteredQuotations, sellerMap]);

  // SKU tab toggle state
  const [skuTab, setSkuTab] = useState("top");

  // SKU performance data
  const { topSkus, slowMovers } = useMemo(() => {
    // Aggregate sales from filtered quotations
    const skuSales = {};
    filteredQuotations.forEach(q => {
      (q.items || []).forEach(item => {
        const sid = item.sku_id;
        if (!sid) return;
        if (selectedBrands.length > 0 && !selectedBrands.includes(item.brand)) return;
        if (selectedSkus.length > 0 && !selectedSkus.includes(sid)) return;
        if (!skuSales[sid]) skuSales[sid] = { sku_id: sid, product_name: item.product_name, brand: item.brand || "Unknown", qtySold: 0, revenue: 0 };
        skuSales[sid].qtySold += item.quantity || 0;
        skuSales[sid].revenue += item.invoice_value || 0;
      });
    });

    // Top selling: sorted by revenue desc
    const allSold = Object.values(skuSales).sort((a, b) => b.revenue - a.revenue);
    const top = allSold.slice(0, 15);

    // Slow movers: zero-sales SKUs + bottom 10% by qty
    let productsToCheck = allProducts;
    if (selectedBrands.length > 0) productsToCheck = productsToCheck.filter(p => selectedBrands.includes(p.brand));
    if (selectedSkus.length > 0) productsToCheck = productsToCheck.filter(p => selectedSkus.includes(p.sku_id));

    const zeroSales = productsToCheck
      .filter(p => p.sku_id && !skuSales[p.sku_id])
      .map(p => ({ sku_id: p.sku_id, product_name: p.product_name, brand: p.brand || "Unknown", qtySold: 0, revenue: 0 }));

    const qtyThreshold = allSold.length > 0 ? allSold[Math.floor(allSold.length * 0.9)]?.qtySold || 0 : 0;
    const lowSales = allSold.filter(s => s.qtySold > 0 && s.qtySold <= qtyThreshold);

    const slow = [...zeroSales, ...lowSales.sort((a, b) => a.qtySold - b.qtySold)].slice(0, 15);

    return { topSkus: top, slowMovers: slow };
  }, [filteredQuotations, allProducts, selectedBrands, selectedSkus]);

  return (
    <div className="space-y-5">
      {filterBar}

      {/* 5 KPI CARDS */}
      <div className="grid grid-cols-5 gap-3" data-testid="kpi-cards-row">
        {cards.map((c, idx) => {
          const Icon = c.icon;
          return (
            <div
              key={c.key}
              onClick={() => navigate(c.route)}
              className="cursor-pointer rounded-lg p-3 shadow-sm hover:shadow-md transition-all"
              style={{ background: c.bg, border: `1px solid ${c.color}33` }}
              data-testid={`kpi-card-${idx}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="p-1.5 rounded" style={{ background: `${c.color}1F`, border: `1px solid ${c.color}40` }}><Icon className="h-3.5 w-3.5" style={{ color: c.color }} /></div>
                <ArrowUpRight className="h-3.5 w-3.5 text-[#1A1A1A]/30" />
              </div>
              <p className="text-[11px] text-[#1A1A1A]/55 font-medium mb-0.5">{c.label}</p>
              <p className="text-lg font-bold text-[#0F0F0F] font-mono leading-tight mb-1">{c.value}</p>
              <div className="mb-1.5">{c.delta}</div>
              <Sparkline data={c.spark} color={c.warn ? "#BA7517" : c.color} />
            </div>
          );
        })}
      </div>

      {/* Sales Trend */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <div>
            <CardTitle className="text-base font-semibold">Sales Trend</CardTitle>
            <p className="text-xs text-muted-foreground">Invoice value over time{hasActiveFilters&&" (filtered)"}</p>
            {dateRangeLabel && <p className="text-[11px] text-muted-foreground/70 mt-0.5">{dateRangeLabel}</p>}
          </div>
          <Select value={timeGranularity} onValueChange={setTimeGranularity}><SelectTrigger className="w-24 h-7 text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="day">Daily</SelectItem><SelectItem value="week">Weekly</SelectItem><SelectItem value="month">Monthly</SelectItem></SelectContent></Select>
        </CardHeader>
        <CardContent>
          {salesTrendData.length > 0 ? (
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={salesTrendData} margin={{top:5,right:20,left:0,bottom:5}}>
                  <defs><linearGradient id="tealG" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#1D9E75" stopOpacity={0.15}/><stop offset="95%" stopColor="#1D9E75" stopOpacity={0}/></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E8" />
                  <XAxis dataKey="label" tick={{fontSize:10,fill:"#1A1A1A80"}} tickLine={false} axisLine={{stroke:"#E8E8E8"}} interval={salesTrendData.length>15?Math.floor(salesTrendData.length/8):0} />
                  <YAxis tickFormatter={formatCurrency} tick={{fontSize:10,fill:"#1A1A1A80"}} tickLine={false} axisLine={false} width={70} />
                  <Tooltip content={<SalesTrendTooltip />} />
                  <Area type="monotone" dataKey="revenue" stroke="#1D9E75" strokeWidth={2} fill="url(#tealG)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : <div className="h-[260px] flex items-center justify-center text-muted-foreground text-sm">No data</div>}
        </CardContent>
      </Card>

      {/* Top Sellers Section */}
      <div>
        <h2 className="text-base font-semibold text-[#0F0F0F] mb-0.5">Top Sellers</h2>
        <p className="text-xs text-muted-foreground">Who is driving the most revenue</p>
        {dateRangeLabel && <p className="text-[11px] text-muted-foreground/70 mt-0.5 mb-3">{dateRangeLabel}</p>}
        {!dateRangeLabel && <div className="mb-3" />}
        <div className="grid gap-4 lg:grid-cols-2 items-stretch">
          {/* Panel 1: Top 10 by Revenue */}
          <Card className="flex flex-col">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold">Top Sellers by Revenue</CardTitle>
              <p className="text-xs text-muted-foreground">Based on total invoice value</p>
            </CardHeader>
            <CardContent className="flex-1">
              {topSellers.length > 0 ? (
                <div className="h-[320px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={topSellers.slice(0, 5)} layout="vertical" margin={{ top: 5, right: 60, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E8" horizontal={false} />
                      <XAxis type="number" tickFormatter={formatCurrency} tick={{ fontSize: 10, fill: "#1A1A1A80" }} tickLine={false} axisLine={{ stroke: "#E8E8E8" }} />
                      <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11, fill: "#1A1A1A" }} tickLine={false} axisLine={false} />
                      <Tooltip content={<TopSellersTooltip />} cursor={{ fill: "rgba(50, 180, 172, 0.05)" }} />
                      <Bar dataKey="revenue" fill="#32B4AC" radius={[0, 4, 4, 0]} barSize={28} cursor="pointer" onClick={() => navigate("/seller-insights")} label={{ position: "right", formatter: formatCurrency, fontSize: 10, fill: "#1A1A1A80" }} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[320px] flex items-center justify-center text-sm text-muted-foreground">No seller data</div>
              )}
            </CardContent>
          </Card>

          {/* Panel 2: Sellers Above 62% Discount */}
          <Card className="flex flex-col" style={{ background: discountAlertSellers.length > 0 ? "#FFFCF5" : undefined }}>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                {discountAlertSellers.length > 0 && <AlertTriangle className="h-4 w-4" style={{ color: "#BA7517" }} />}
                <CardTitle className="text-base font-semibold">Sellers Above 62% Discount</CardTitle>
              </div>
              <p className="text-xs text-muted-foreground">Sellers with avg. effective discount exceeding 62%</p>
            </CardHeader>
            <CardContent className="flex-1">
              {discountAlertSellers.length > 0 ? (
                <div className="max-h-[300px] overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#FAEEDA]/30">
                        <TableHead className="text-xs">Seller Name</TableHead>
                        <TableHead className="text-xs text-right">No. of Invoices</TableHead>
                        <TableHead className="text-xs text-right">Avg. Discount %</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {discountAlertSellers.map((s, idx) => (
                        <TableRow key={idx} className="cursor-pointer hover:bg-[#FAEEDA]/20" onClick={() => navigate("/discount-analytics")}>
                          <TableCell className="text-sm">{s.name}</TableCell>
                          <TableCell className="text-sm text-right font-mono">{s.invoiceCount}</TableCell>
                          <TableCell className="text-sm text-right font-mono font-semibold" style={{ color: s.avgDiscount >= 75 ? "#ef4444" : "#BA7517" }}>
                            {s.avgDiscount.toFixed(1)}%
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="h-[300px] flex flex-col items-center justify-center gap-2">
                  <CheckCircle2 className="h-8 w-8 text-green-500" />
                  <p className="text-sm text-green-700 font-medium">All sellers are within the 62% discount threshold</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* SKU Performance Section */}
      <div>
        <h2 className="text-base font-semibold text-[#0F0F0F] mb-0.5">SKU Performance</h2>
        <p className="text-xs text-muted-foreground">Best and worst performing products</p>
        {dateRangeLabel && <p className="text-[11px] text-muted-foreground/70 mt-0.5 mb-3">{dateRangeLabel}</p>}
        {!dateRangeLabel && <div className="mb-3" />}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-1 w-fit">
              <button onClick={() => setSkuTab("top")} className="px-3 py-1.5 rounded-md text-xs font-medium transition-colors" style={skuTab === "top" ? { background: "#32B4AC", color: "#FFFFFF", border: "0.5px solid #32B4AC" } : { background: "transparent", color: "#1A1A1A", border: "0.5px solid #E8E8E8" }} data-testid="sku-tab-top">Top Selling SKUs</button>
              <button onClick={() => setSkuTab("slow")} className="px-3 py-1.5 rounded-md text-xs font-medium transition-colors" style={skuTab === "slow" ? { background: "#32B4AC", color: "#FFFFFF", border: "0.5px solid #32B4AC" } : { background: "transparent", color: "#1A1A1A", border: "0.5px solid #E8E8E8" }} data-testid="sku-tab-slow">Slow Movers</button>
            </div>
          </CardHeader>
          <CardContent>
            {skuTab === "top" ? (
              topSkus.length > 0 ? (
                <div className="max-h-[400px] overflow-auto">
                  <Table>
                    <TableHeader><TableRow className="bg-slate-50"><TableHead className="text-xs w-8">#</TableHead><TableHead className="text-xs">SKU Name</TableHead><TableHead className="text-xs">SKU ID</TableHead><TableHead className="text-xs">Brand</TableHead><TableHead className="text-xs text-right">Qty Sold</TableHead><TableHead className="text-xs text-right">Revenue</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {topSkus.map((s, i) => (
                        <TableRow key={s.sku_id}>
                          <TableCell className="text-xs text-muted-foreground">{i + 1}</TableCell>
                          <TableCell className="text-sm">{s.product_name}</TableCell>
                          <TableCell className="text-xs font-mono text-muted-foreground">{s.sku_id}</TableCell>
                          <TableCell>{(() => { const p = getBrandPillStyle(s.brand); return <span className="px-2 py-0.5 rounded-full text-[10px] font-medium" style={{ background: p.bg, color: p.text }}>{s.brand}</span>; })()}</TableCell>
                          <TableCell className="text-sm text-right font-mono">{s.qtySold.toLocaleString("en-IN")}</TableCell>
                          <TableCell className="text-sm text-right font-mono">{formatCurrency(s.revenue)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="py-12 text-center text-sm text-muted-foreground">No SKU data for this period</div>
              )
            ) : (
              slowMovers.length > 0 ? (
                <div className="max-h-[400px] overflow-auto">
                  <Table>
                    <TableHeader><TableRow className="bg-slate-50"><TableHead className="text-xs w-8">#</TableHead><TableHead className="text-xs">SKU Name</TableHead><TableHead className="text-xs">SKU ID</TableHead><TableHead className="text-xs">Brand</TableHead><TableHead className="text-xs text-right">Qty Sold</TableHead><TableHead className="text-xs text-right">Revenue</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {slowMovers.map((s, i) => (
                        <TableRow key={s.sku_id} style={s.qtySold === 0 ? { background: "#FEF2F2" } : undefined}>
                          <TableCell className="text-xs text-muted-foreground">{i + 1}</TableCell>
                          <TableCell className="text-sm">{s.product_name}</TableCell>
                          <TableCell className="text-xs font-mono text-muted-foreground">{s.sku_id}</TableCell>
                          <TableCell>{(() => { const p = getBrandPillStyle(s.brand); return <span className="px-2 py-0.5 rounded-full text-[10px] font-medium" style={{ background: p.bg, color: p.text }}>{s.brand}</span>; })()}</TableCell>
                          <TableCell className={`text-sm text-right font-mono ${s.qtySold === 0 ? "text-red-500 font-semibold" : ""}`}>{s.qtySold === 0 ? "0" : s.qtySold.toLocaleString("en-IN")}</TableCell>
                          <TableCell className={`text-sm text-right font-mono ${s.qtySold === 0 ? "text-red-500" : ""}`}>{s.qtySold === 0 ? "₹0" : formatCurrency(s.revenue)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="py-12 flex flex-col items-center justify-center gap-2">
                  <CheckCircle2 className="h-8 w-8 text-green-500" />
                  <p className="text-sm text-green-700 font-medium">No slow-moving SKUs in this period</p>
                </div>
              )
            )}
          </CardContent>
        </Card>
      </div>

      {/* Brand Revenue Donut + Table */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base font-semibold">Revenue by Brand</CardTitle></CardHeader>
          <CardContent>
            {brandData.length > 0 ? (
              <div className="h-[240px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart><Pie data={brandData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={2} dataKey="value">{brandData.map((e,i)=><Cell key={i} fill={BRAND_COLORS[e.name]||PIE_COLORS[i%PIE_COLORS.length]} />)}</Pie><Tooltip content={({ active, payload }) => {
                    if (!active || !payload || !payload.length) return null;
                    const d = payload[0].payload;
                    return (
                      <div data-testid="dashboard-brand-pie-tooltip" className="bg-white border border-slate-200 rounded-lg shadow-md px-3 py-2 text-xs">
                        <div className="font-semibold text-slate-900 mb-1">{d.name}</div>
                        <div className="text-slate-600">Revenue: <span className="font-mono text-slate-900">{formatCurrency(d.value)}</span></div>
                        <div className="text-slate-600">Contribution: <span className="font-mono text-slate-900">{Number(d.percentage).toFixed(1)}%</span></div>
                      </div>
                    );
                  }} contentStyle={{borderRadius:8,border:"1px solid #E8E8E8",fontSize:12}} /><Legend formatter={v=><span className="text-xs">{v}</span>} /></PieChart>
                </ResponsiveContainer>
              </div>
            ) : <div className="h-[240px] flex items-center justify-center text-sm text-muted-foreground">No data</div>}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base font-semibold">Brand Performance</CardTitle></CardHeader>
          <CardContent>
            <div className="max-h-[240px] overflow-auto">
              <Table><TableHeader><TableRow className="bg-slate-50"><TableHead className="text-xs">Brand</TableHead><TableHead className="text-xs text-right">Revenue</TableHead><TableHead className="text-xs text-right">Share</TableHead></TableRow></TableHeader>
              <TableBody>{brandData.map(b=>(<TableRow key={b.name} data-testid={`brand-perf-row-${b.name}`}><TableCell className="text-sm"><div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-full" style={{background:BRAND_COLORS[b.name]||"#94a3b8"}} />{b.name}</div></TableCell><TableCell className="text-right text-sm font-mono">{formatCurrency(b.value)}</TableCell><TableCell className="text-right text-sm font-mono">{b.percentage}%</TableCell></TableRow>))}</TableBody></Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
